import { useState, useEffect, useRef } from "react";
import useApi from "./useApi";

const useFilter = (data, filters, filterConfig = {}, activeTab, tabConfig = {}) => {
    const [result, setResult] = useState([]);
    const [counts, setCounts] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [localData, setLocalData] = useState(data);
    const workerRef = useRef(null);
    const { callApi } = useApi();
    const pendingRequests = useRef(new Set());
    const isMounted = useRef(false);

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
            workerRef.current?.terminate();
            pendingRequests.current.clear();
        };
    }, []);

    useEffect(() => {
        setLocalData(data);
    }, [data]);

    useEffect(() => {
        const worker = new Worker(new URL("../workers/filtering.worker.js", import.meta.url), {
            type: "module"
        });
        workerRef.current = worker;

        const handleMessage = async (e) => {
            if (!isMounted.current) return;

            // Always update results and counts first
            if (e.data.result) setResult(e.data.result);
            if (e.data.counts) setCounts(e.data.counts);
            // Handle API requests if needed
            if (e.data.apiRequestsNeeded?.length) {
                for (const req of e.data.apiRequestsNeeded) {
                    const requestKey = `${req.model}:${req.terms.join('-')}`;
                    if (pendingRequests.current.has(requestKey)) continue;
                    pendingRequests.current.add(requestKey);

                    try {
                        const response = await callApi(
                            req.endpoint,
                            "POST",
                            {
                                model: req.model,
                                terms: req.terms,
                                field: req.itemKey
                            }
                        );
                        if (isMounted.current && response?.data?.length) {
                            const updateState = (prev) => {
                                const existingIds = new Set(prev.map(item => item._id));
                                const newItems = response.data.map(item => ({
                                    ...item,
                                    _apiSourced: true
                                })).filter(newItem => !existingIds.has(newItem._id));

                                // Only update if there are actually new items
                                return newItems.length > 0 ? [...prev, ...newItems] : prev;
                            };

                            setLocalData(updateState);
                            setResult(updateState);
                        }
                    } catch (error) {
                        console.error("API request failed:", error);
                    } finally {
                        pendingRequests.current.delete(requestKey);
                    }
                }
            }
            setIsLoading(false);
        };

        worker.addEventListener('message', handleMessage);
        return () => {
            worker.removeEventListener('message', handleMessage);
            worker.terminate();
        };
    }, []);

    useEffect(() => {
        if (workerRef.current && isMounted.current) {
            setIsLoading(true);
            workerRef.current.postMessage({
                type: "combined",
                data: localData,
                filters,
                filterConfig,
                activeTab,
                tabConfig,
            });
        }
    }, [localData, filters, filterConfig, activeTab, tabConfig]);

    return { data: result, counts, isLoading };
};

export default useFilter;